import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faTrashCan, faUser } from '@fortawesome/pro-solid-svg-icons';
import { BannerService } from '@terminus-ui/banner';
import {
  PageHeaderButtonsComponent,
  PageHeaderComponent,
} from '@terminus-ui/page-header';
import { Button, ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { column, TableWrapperComponent } from 'table-wrapper';
import { AddUsersDialogComponent } from '../add-users-dialog/add-users-dialog.component';
import { ApplicationManagerApiService } from '../application-manager-api.service';
import { CustomerService } from '../customer.service';
import { DeleteUserDialogComponent } from '../delete-user-dialog/delete-user-dialog.component';
import { UpdateUserDialogComponent } from '../update-user-dialog/update-user-dialog.component';

@Component({
  selector: 'lib-customer-users',
  templateUrl: './customer-users.component.html',
  standalone: true,
  imports: [
    AsyncPipe,
    Button,
    PageHeaderButtonsComponent,
    PageHeaderComponent,
    TableWrapperComponent,
    ToggleSwitchModule,
    NgClass,
    FormsModule,
    ButtonModule,
    FaIconComponent,
  ],
  providers: [DialogService],
})
export class CustomerUsersComponent implements OnInit {
  loading: boolean;
  protected readonly faTrashCan = faTrashCan;
  protected readonly faUser = faUser;
  private customerService = inject(CustomerService);
  private applicationManagerApi = inject(ApplicationManagerApiService);
  private activatedRoute = inject(ActivatedRoute);
  private dialogService = inject(DialogService);
  private bannerService = inject(BannerService);

  @ViewChild('switchRenderer', { read: TemplateRef, static: false })
  private switchRenderer!: TemplateRef<unknown>;
  @ViewChild('actionsRenderer', { read: TemplateRef, static: false })
  private actionsRenderer!: TemplateRef<unknown>;

  private customerID$ = new BehaviorSubject<string>('');
  private refreshCustomerData$ = new Subject<boolean>();
  customerData$ = combineLatest([
    this.customerID$.pipe(filter(Boolean)),
    this.refreshCustomerData$.pipe(startWith(false)),
  ]).pipe(
    switchMap(([customerId, refresh]) => {
      return this.customerService.getCustomerById(customerId, refresh);
    }),
    tap(() => this.refreshUserData$.next(true)),
    filter(Boolean),
    shareReplay(1),
  );
  private refreshUserData$ = new Subject<boolean>();
  usersData$ = combineLatest([this.customerData$, this.refreshUserData$]).pipe(
    switchMap(([customer]) => {
      this.loading = true;
      return this.applicationManagerApi
        .listAllCustomerUsers(customer?.customerId || '')
        .pipe(
          map((users) => {
            this.loading = false;
            return users.map((user) => {
              return {
                id: user.userId || '',
                name: user.name || '',
                email: user.email || '',
                capitalizedName:
                  user.name
                    ?.split(' ')
                    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
                    .join(' ') || '',
                isAdmin: user.isAdmin || false,
                isInternal: user.isInternal || false,
                ...user.instanceAccess,
              };
            });
          }),
        );
    }),
    filter(Boolean),
    shareReplay(1),
  );

  columns$: Observable<column[]> = combineLatest([this.customerData$]).pipe(
    switchMap(([customerData]) => {
      return this.customerService
        .getCustomerAppInstances(customerData?.customerId || '', false)
        .pipe(
          map((instances) => {
            const columns: column[] = [
              { field: 'id', header: 'ID' },
              { field: 'capitalizedName', header: 'User' },
              { field: 'email', header: 'Email' },
              {
                field: 'isAdmin',
                header: 'Admin',
                customCellRenderer: this.switchRenderer,
              },
            ];
            if (instances) {
              for (const instance of instances) {
                columns.push({
                  field: instance.instanceId || '',
                  header: `${instance.name} (${instance.type})` || '',
                  customCellRenderer: this.switchRenderer,
                } as column);
              }
            }
            // add Internal Admin Last
            columns.push({
              field: 'isInternal',
              header: 'Internal Admin',
              customCellRenderer: this.switchRenderer,
            } as column);
            columns.push({
              field: 'actions',
              header: '',
              customCellRenderer: this.actionsRenderer,
            } as column);
            return columns;
          }),
        );
    }),
    filter(Boolean),
    shareReplay(1),
  );

  defaultColumns$: Observable<string[]> = this.columns$.pipe(
    switchMap((columns) => {
      if (!columns) {
        return [];
      }
      // Remove the ID column from the default columns
      return of(
        columns
          .filter((column) => column.field !== 'id')
          .map((column) => column.header as string),
      );
    }),
    filter(Boolean),
    shareReplay(1),
  );

  ngOnInit(): void {
    firstValueFrom(this.activatedRoute.paramMap).then((paramMap) => {
      if (paramMap.has('id')) this.customerID$.next(paramMap.get('id') || '');
    });
  }

  addUsers() {
    this.dialogService
      .open(AddUsersDialogComponent, {
        header: 'Add Users',
        width: '40rem',
        style: { 'min-height': 'fit-content' },
        contentStyle: { overflow: 'visible' },
        data: { customer: this.customerData$ },
        closable: true,
      })
      .onClose.subscribe((closeValue) => {
        if (closeValue) this.refreshUserData$.next(true);
      });
  }

  updateUser(row: any) {
    if (!row) return;

    this.dialogService
      .open(UpdateUserDialogComponent, {
        header: 'Update User',
        width: '40rem',
        style: { 'min-height': 'fit-content' },
        contentStyle: { overflow: 'visible' },
        data: { user: row['capitalizedName'], email: row['email'] },
      })
      .onClose.subscribe(({ update, email, user }) => {
        if (update) {
          this.customerService.updateUser(row['id'], email, user).subscribe({
            next: () => {
              this.bannerService.pushBanner({
                isClosable: true,
                isFloating: false,
                title: `Successfully updated the user ${row['capitalizedName']}.`,
                type: 'success',
                enableAutoDismissWithDuration: 10000,
              });
              this.refreshUserData$.next(true);
            },
            error: (error) => {
              this.bannerService.pushBanner({
                isClosable: true,
                isFloating: false,
                title: `Failed to update the user ${row['capitalizedName']}.`,
                type: 'error',
                enableAutoDismissWithDuration: 10000,
              });
            },
            complete: () => {},
          });
        }
      });
  }

  deleteUser(row: any) {
    if (!row) return;

    this.dialogService
      .open(DeleteUserDialogComponent, {
        header: 'Delete User',
        width: '25rem',
        style: { 'min-height': 'fit-content' },
        contentStyle: { overflow: 'visible' },
        data: { user: row['capitalizedName'] },
        closable: true,
      })
      .onClose.subscribe((closeValue) => {
        if (closeValue) {
          firstValueFrom(this.customerID$).then((customerId) => {
            this.customerService.deleteUser(customerId, row['id']).subscribe({
              next: () => {
                this.bannerService.pushBanner({
                  isClosable: true,
                  isFloating: false,
                  title: `Successfully deleted the user ${row['capitalizedName']}.`,
                  type: 'success',
                  enableAutoDismissWithDuration: 10000,
                });
                this.refreshUserData$.next(true);
              },
              error: (error) => {
                this.bannerService.pushBanner({
                  isClosable: true,
                  isFloating: false,
                  title: `Failed to delete the user ${row['capitalizedName']}.`,
                  type: 'error',
                  enableAutoDismissWithDuration: 10000,
                });
              },
              complete: () => {},
            });
          });
        }
      });
  }

  onToggleChange($event: any, row: any, field: string) {
    if (!row || !field) return;

    if (field === 'isAdmin') {
      firstValueFrom(this.customerID$).then((customerId) => {
        this.customerService
          .toggleUserAdmin(customerId, row['id'], $event)
          .subscribe({
            next: () => {
              this.bannerService.pushBanner({
                isClosable: true,
                isFloating: false,
                title: `Successfully toggled admin for ${row['capitalizedName']}.`,
                type: 'success',
                enableAutoDismissWithDuration: 10000,
              });
            },
            error: (error) => {
              this.bannerService.pushBanner({
                isClosable: true,
                isFloating: false,
                title: `Failed to toggle admin for ${row['capitalizedName']}.`,
                type: 'error',
                enableAutoDismissWithDuration: 10000,
              });
            },
            complete: () => {},
          });
      });
    } else if (field === 'isInternal') {
      firstValueFrom(this.customerID$).then((customerId) => {
        this.applicationManagerApi
          .toggleUserInternalAdmin(customerId, row['id'], $event)
          .subscribe({
            next: () => {
              this.bannerService.pushBanner({
                isClosable: true,
                isFloating: false,
                title: `Successfully toggled Internal Admin for ${row['capitalizedName']}.`,
                type: 'success',
                enableAutoDismissWithDuration: 10000,
              });
            },
            error: (error) => {
              this.bannerService.pushBanner({
                isClosable: true,
                isFloating: false,
                title: `Failed to toggle Internal Admin for ${row['capitalizedName']}.`,
                type: 'error',
                enableAutoDismissWithDuration: 10000,
              });
            },
            complete: () => {},
          });
      });
    } else {
      this.customerService
        .toggleUserAccess(row['id'], field, $event)
        .subscribe({
          next: () => {
            this.bannerService.pushBanner({
              isClosable: true,
              isFloating: false,
              title: `Successfully toggled user access for ${row['capitalizedName']}.`,
              type: 'success',
              enableAutoDismissWithDuration: 10000,
            });
          },
          error: (error) => {
            this.bannerService.pushBanner({
              isClosable: true,
              isFloating: false,
              title: `Failed to toggle user access for ${row['capitalizedName']}.`,
              type: 'error',
              enableAutoDismissWithDuration: 10000,
            });
          },
          complete: () => {},
        });
    }
  }
}
